'use client';

import {
  User, ExternalLink, Phone, Mail, MapPin, Tag, FileText, Receipt,
  CalendarCheck, MessageSquare, Brain, TrendingUp, Sparkles, Clock,
  PhoneCall,
} from 'lucide-react';

const COPILOT_BASE = 'https://secure.copilotcrm.com';

// Shape returned by the customer_context_load tool. Mirrors what
// server/lib/customer-context.js produces. Keep in sync.
export interface CustomerContextData {
  customerId: string;
  profile: {
    id: string;
    name: string;
    type: string;
    status: string;
    tags: string[];
    phone: string;
    email: string;
    address: string;
    propertyId: string | null;
    sqft: number | null;
    notes: string;
  } | null;
  communications: {
    recentSms: unknown[];
    recentTimeline: Array<{ type?: string; date?: string; author?: string; content?: string; entryId?: string }>;
  };
  work: {
    estimates: Array<{ id?: string | number; date?: string; total?: number; status?: string }>;
    invoices: Array<{ id?: string | number; date?: string; total?: number; paid?: number; status?: string }>;
    visits: {
      past90d: unknown[];
      next90d: unknown[];
    };
  };
  memoryFacts: Array<{ kind?: string; title?: string; body?: string; fact?: string; content?: string }>;
  derived: {
    lifetimeValue: number;
    stage: string;
    lifetimeValueSource?: 'cached' | 'partial' | 'computed';
    lifetimeValueComputedAt?: string | null;
    lifetimeInvoiceCount?: number | null;
  };
  _slices?: Record<string, boolean>;
  _loadedAt?: string;
  _cached?: boolean;
}

interface Props {
  data: CustomerContextData;
}

function fmtUSD(n: number | undefined | null): string {
  if (typeof n !== 'number' || !isFinite(n)) return '$0';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function StageBadge({ stage }: { stage: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    lead:    { bg: 'rgba(99,102,241,0.15)',  fg: '#818cf8' },
    active:  { bg: 'rgba(34,197,94,0.15)',   fg: '#22c55e' },
    dormant: { bg: 'rgba(245,158,11,0.15)',  fg: '#f59e0b' },
    churned: { bg: 'rgba(148,163,184,0.18)', fg: '#94a3b8' },
  };
  const c = colors[stage] || colors.lead;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded"
      style={{ background: c.bg, color: c.fg }}
    >
      {stage}
    </span>
  );
}

export default function CustomerContextCard({ data }: Props) {
  if (!data || !data.profile) {
    return null; // bundle failed; agent's text already explains
  }

  const p = data.profile;
  const work = data.work || { estimates: [], invoices: [], visits: { past90d: [], next90d: [] } };
  const comms = data.communications || { recentSms: [], recentTimeline: [] };
  const memory = data.memoryFacts || [];
  const derived = data.derived || { lifetimeValue: 0, stage: 'lead' };

  return (
    <div
      className="rounded-xl border overflow-hidden mb-1.5"
      style={{ background: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: 'var(--border-primary)', background: 'rgba(0, 194, 168, 0.06)' }}
      >
        <User size={14} style={{ color: '#00C2A8' }} />
        <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#00C2A8' }}>
          Customer Context
        </span>
        {data._cached && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
            <Clock size={9} /> cached
          </span>
        )}
      </div>

      <div className="px-3 py-3 space-y-3">
        {/* Identity row */}
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[14px] font-semibold text-[--text-primary]">{p.name || `Customer #${p.id}`}</p>
            <div className="flex items-center gap-2">
              <StageBadge stage={derived.stage} />
              <span className="text-[9px] text-[--text-secondary] font-mono">#{p.id}</span>
            </div>
          </div>
          {(p.phone || p.email || p.address) && (
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[--text-secondary]">
              {p.phone   && <span className="inline-flex items-center gap-1"><Phone size={10} />{p.phone}</span>}
              {p.email   && <span className="inline-flex items-center gap-1"><Mail size={10} />{p.email}</span>}
              {p.address && <span className="inline-flex items-center gap-1"><MapPin size={10} />{p.address}</span>}
            </div>
          )}
          <a
            href={`${COPILOT_BASE}/customers/details/${p.id}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-medium mt-0.5 self-start"
            style={{ color: '#00C2A8' }}
          >
            <ExternalLink size={10} /> View in CRM
          </a>
        </div>

        {/* Tags */}
        {p.tags && p.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {p.tags.map((t, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: 'var(--bg-main)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
              >
                <Tag size={9} />{t}
              </span>
            ))}
          </div>
        )}

        {/* Property */}
        {(p.propertyId || p.sqft) && (
          <div
            className="flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[11px]"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <span className="text-[--text-secondary] uppercase tracking-wide text-[9px]">Property</span>
            {p.propertyId && <span className="font-mono text-[--text-primary]">#{p.propertyId}</span>}
            {p.sqft && <span className="text-[--text-primary]">{p.sqft.toLocaleString()} sqft</span>}
          </div>
        )}

        {/* Stats grid: lifetime value, recent SMS, estimates, invoices, visits */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat
            label={derived.lifetimeValueSource === 'partial' ? 'Lifetime (approx)' : 'Lifetime'}
            value={fmtUSD(derived.lifetimeValue)}
            icon={<TrendingUp size={11} />}
          />
          <Stat label="Recent SMS" value={String(comms.recentSms.length)} icon={<MessageSquare size={11} />} />
          <Stat label="Estimates" value={String(work.estimates.length)} icon={<FileText size={11} />} />
          <Stat label="Invoices" value={String(work.invoices.length)} icon={<Receipt size={11} />} />
        </div>

        {/* Visits row */}
        {(work.visits.past90d.length > 0 || work.visits.next90d.length > 0) && (
          <div
            className="flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[11px]"
            style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
          >
            <CalendarCheck size={11} style={{ color: '#00C2A8' }} />
            <span className="text-[--text-primary]">
              {work.visits.past90d.length} past 90d
            </span>
            <span className="text-[--text-secondary]">·</span>
            <span className="text-[--text-primary]">
              {work.visits.next90d.length} upcoming 90d
            </span>
          </div>
        )}

        {/* Estimate summary row */}
        {work.estimates.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
              Recent estimates
            </p>
            <div className="space-y-1">
              {work.estimates.slice(0, 3).map((e, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-[11px]"
                  style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
                >
                  <span className="text-[--text-primary]">{e.date || '(no date)'}</span>
                  <span className="font-mono text-[--text-primary]">{fmtUSD(e.total)}</span>
                  <span className="text-[10px] uppercase" style={{ color: 'var(--text-secondary)' }}>{e.status || ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call notes (filtered from timeline). Surfaces internal-only call
           notes operators have logged on the customer. */}
        {(() => {
          const timeline = comms.recentTimeline || [];
          const callNotes = timeline.filter(t => /call|note|phone/i.test(String(t?.type || '')));
          if (callNotes.length === 0) return null;
          return (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                <PhoneCall size={10} /> Call notes ({callNotes.length})
              </p>
              <div className="space-y-1">
                {callNotes.slice(0, 3).map((n, i) => (
                  <div
                    key={n.entryId || i}
                    className="px-2.5 py-1 rounded-lg text-[11px]"
                    style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
                  >
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-[9px] uppercase" style={{ color: '#00C2A8' }}>{n.type}</span>
                      <span className="text-[9px]" style={{ color: 'var(--text-tertiary)' }}>
                        {n.date || ''}{n.author ? ` · ${n.author}` : ''}
                      </span>
                    </div>
                    {n.content && (
                      <div className="text-[--text-secondary] whitespace-pre-wrap">
                        {String(n.content).length > 200 ? String(n.content).slice(0, 200) + '…' : n.content}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Memory facts (what the agent has previously learned) */}
        {memory.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
              <Brain size={10} /> Agent memory ({memory.length})
            </p>
            <div className="space-y-1">
              {memory.slice(0, 3).map((m, i) => {
                const headline = m.title || m.fact || m.content || '';
                const detail = m.body || '';
                const fallback = !headline && !detail ? '(empty)' : '';
                return (
                  <div
                    key={i}
                    className="px-2.5 py-1 rounded-lg text-[11px]"
                    style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
                  >
                    {m.kind && <span className="text-[9px] uppercase mr-1.5" style={{ color: '#00C2A8' }}>{m.kind}</span>}
                    {headline && <span className="text-[--text-primary]">{headline}</span>}
                    {headline && detail && <span className="mx-1">·</span>}
                    {detail && <span>{detail.length > 140 ? detail.slice(0, 140) + '…' : detail}</span>}
                    {fallback}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes from profile */}
        {p.notes && (
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
              <Sparkles size={10} /> Profile notes
            </p>
            <div
              className="px-2.5 py-1.5 rounded-lg text-[11px] whitespace-pre-wrap"
              style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)' }}
            >
              {p.notes}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <div
      className="flex flex-col gap-0.5 px-2.5 py-1.5 rounded-lg"
      style={{ background: 'var(--bg-main)', border: '1px solid var(--border-primary)' }}
    >
      <span className="text-[9px] uppercase tracking-wide flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
        {icon}{label}
      </span>
      <span className="text-[13px] font-semibold text-[--text-primary]">{value}</span>
    </div>
  );
}
